import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { useNotificationStore } from '@/store/notification-store'
import { BellIcon, InboxIcon } from '@/shared/components/icons'
import { AccentStrip } from '@/shared/components/ui'
import { cn } from '@/shared/lib/utils'

const PREVIEW_COUNT = 5

// Desktop only. A glance without leaving the page: the full list stays a real route
// because push notifications link to it, but scanning what just arrived should not cost
// a navigation and a trip back. On mobile the header has no room for a second control, so
// notifications fold into the account avatar instead.
export function NotificationBell() {
  const navigate = useNavigate()
  const notifications = useNotificationStore((s) => s.notifications)
  const unreadCount = useNotificationStore((s) => s.unreadCount)
  const markRead = useNotificationStore((s) => s.markRead)

  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return

    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false)
        triggerRef.current?.focus()
      }
    }

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('touchstart', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('touchstart', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const preview = notifications.slice(0, PREVIEW_COUNT)

  const openItem = (id: string, linkPath: string | null) => {
    setOpen(false)
    void markRead(id)
    navigate(linkPath ?? '/notifications')
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
        className={cn(
          'relative flex h-11 w-11 items-center justify-center rounded-full transition-colors',
          open ? 'text-brand-600 ring-1 ring-inset ring-brand-200' : 'text-neutral-500 hover:text-navy-900'
        )}
      >
        <BellIcon className="h-5 w-5" aria-hidden="true" />
        {unreadCount > 0 && (
          <span className="absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger-600 px-1 text-[10px] font-bold text-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Recent notifications"
          className="absolute right-0 top-full z-50 mt-2 w-[22rem] overflow-hidden rounded-2xl border border-line bg-white shadow-float"
        >
          <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
            <p className="text-sm font-bold text-navy-900">Notifications</p>
            {unreadCount > 0 && (
              <span className="text-xs font-semibold text-brand-600">{unreadCount} unread</span>
            )}
          </div>

          {preview.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <span className="mx-auto mb-2 flex h-9 w-9 items-center justify-center text-neutral-400">
                <InboxIcon className="h-6 w-6" aria-hidden="true" />
              </span>
              <p className="text-sm font-semibold text-navy-900">Nothing yet</p>
              <p className="mt-0.5 text-xs text-neutral-500">
                Updates on your enquiries and payouts will appear here.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-line">
              {preview.map((n) => {
                const unread = !n.read_at
                return (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => openItem(n.id, n.link_path)}
                      className="group relative block w-full py-3 pl-5 pr-4 text-left transition-colors hover:bg-neutral-50"
                    >
                      {unread && <AccentStrip tone="brand" />}
                      <p className={cn('text-sm text-navy-900', unread ? 'font-bold' : 'font-medium')}>
                        {n.title}
                      </p>
                      {n.body && (
                        <p className="mt-0.5 line-clamp-2 text-xs leading-relaxed text-neutral-600">
                          {n.body}
                        </p>
                      )}
                    </button>
                  </li>
                )
              })}
            </ul>
          )}

          <Link
            to="/notifications"
            onClick={() => setOpen(false)}
            className="block border-t border-line px-4 py-3 text-center text-sm font-semibold text-brand-700 transition-colors hover:bg-neutral-50"
          >
            View all notifications
          </Link>
        </div>
      )}
    </div>
  )
}
